import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChoreCard from './ChoreCard';

function ChoreBoard({
  chores, categories, rooms, loading, statusFilter, onStatusFilterChange,
  onClaim, onUnclaim, onComplete, onReopen, onDelete,
  onToggleChecklistItem, onAddChecklistItem, onDeleteChecklistItem, onAddGoal,
  onEdit, onCreateNew, goals, onAssign, members
}) {
  const { t } = useTranslation();
  const [expandedChoreId, setExpandedChoreId] = useState(null);

  const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));
  const overdueChores = chores.filter(c => c.status !== 2 && c.dueDate && new Date(c.dueDate) < startOfToday);
  const openChores = chores.filter(c => c.status !== 1 && c.status !== 2);
  const claimedChores = chores.filter(c => c.status === 1);
  const completedChores = chores.filter(c => c.status === 2);

  return (
    <div className="chore-board">
      <div className="chore-board-header">
        <button className="chore-btn chore-btn-primary" onClick={onCreateNew}>
          {t('chores.board.newChore')}
        </button>
        <div className="chore-filters">
          <button
            className={`chore-filter-btn ${statusFilter === null ? 'active' : ''}`}
            onClick={() => onStatusFilterChange(null)}
          >
            {t('chores.board.all')} ({chores.length})
          </button>
          {[t('chores.status.open'), t('chores.status.claimed'), t('chores.status.completed')].map((label, idx) => (
            <button
              key={idx}
              className={`chore-filter-btn ${statusFilter === idx ? 'active' : ''}`}
              onClick={() => onStatusFilterChange(idx)}
            >
              {label} ({chores.filter(c => c.status === idx).length})
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="chore-loading">
          <div className="chore-spinner"></div>
          {t('chores.board.loadingChores')}
        </div>
      )}

      {!loading && overdueChores.length > 0 && (
        <div className="chore-overdue-banner">
          {t('chores.board.overdueCount', { count: overdueChores.length })}
        </div>
      )}

      {!loading && chores.length === 0 && (
        <div className="chore-empty">
          <span className="chore-empty-icon">🧹</span>
          <p>{t('chores.board.emptyMessage')}</p>
          <button className="chore-btn chore-btn-primary" onClick={onCreateNew}>
            {t('chores.board.createChore')}
          </button>
        </div>
      )}

      {!loading && statusFilter === null && (
        <>
          {openChores.length > 0 && (
            <ChoreSection title={t('chores.board.sectionOpen')} count={openChores.length} emoji="📋">
              {openChores.map(chore => (
                <ChoreCard
                  key={chore.id}
                  chore={chore}
                  categories={categories}
                  rooms={rooms}
                  expanded={expandedChoreId === chore.id}
                  onToggleExpand={() => setExpandedChoreId(expandedChoreId === chore.id ? null : chore.id)}
                  onClaim={() => onClaim(chore.id)}
                  onComplete={() => onComplete(chore.id)}
                  onDelete={() => onDelete(chore.id)}
                  onEdit={() => onEdit(chore)}
                  onAssign={onAssign}
                  members={members}
                  onToggleChecklistItem={onToggleChecklistItem}
                  onAddChecklistItem={onAddChecklistItem}
                  onDeleteChecklistItem={onDeleteChecklistItem}
                  onAddGoal={onAddGoal}
                  goals={goals}
                />
              ))}
            </ChoreSection>
          )}
          {claimedChores.length > 0 && (
            <ChoreSection title={t('chores.board.sectionInProgress')} count={claimedChores.length} emoji="🔨">
              {claimedChores.map(chore => (
                <ChoreCard
                  key={chore.id}
                  chore={chore}
                  categories={categories}
                  rooms={rooms}
                  expanded={expandedChoreId === chore.id}
                  onToggleExpand={() => setExpandedChoreId(expandedChoreId === chore.id ? null : chore.id)}
                  onUnclaim={() => onUnclaim(chore.id)}
                  onComplete={() => onComplete(chore.id)}
                  onDelete={() => onDelete(chore.id)}
                  onEdit={() => onEdit(chore)}
                  onAssign={onAssign}
                  members={members}
                  onToggleChecklistItem={onToggleChecklistItem}
                  onAddChecklistItem={onAddChecklistItem}
                  onDeleteChecklistItem={onDeleteChecklistItem}
                  onAddGoal={onAddGoal}
                  goals={goals}
                />
              ))}
            </ChoreSection>
          )}
          {completedChores.length > 0 && (
            <ChoreSection title={t('chores.board.sectionCompleted')} count={completedChores.length} emoji="✅">
              {completedChores.map(chore => (
                <ChoreCard
                  key={chore.id}
                  chore={chore}
                  categories={categories}
                  rooms={rooms}
                  expanded={expandedChoreId === chore.id}
                  onToggleExpand={() => setExpandedChoreId(expandedChoreId === chore.id ? null : chore.id)}
                  onReopen={() => onReopen(chore.id)}
                  onDelete={() => onDelete(chore.id)}
                  onToggleChecklistItem={onToggleChecklistItem}
                  onAddChecklistItem={onAddChecklistItem}
                  onDeleteChecklistItem={onDeleteChecklistItem}
                  goals={goals}
                />
              ))}
            </ChoreSection>
          )}
        </>
      )}

      {!loading && statusFilter !== null && (
        <div className="chore-list">
          {chores.map(chore => (
            <ChoreCard
              key={chore.id}
              chore={chore}
              categories={categories}
                  rooms={rooms}
              expanded={expandedChoreId === chore.id}
              onToggleExpand={() => setExpandedChoreId(expandedChoreId === chore.id ? null : chore.id)}
              onClaim={chore.status === 0 ? () => onClaim(chore.id) : undefined}
              onUnclaim={chore.status === 1 ? () => onUnclaim(chore.id) : undefined}
              onComplete={chore.status !== 2 ? () => onComplete(chore.id) : undefined}
              onReopen={chore.status === 2 ? () => onReopen(chore.id) : undefined}
              onDelete={() => onDelete(chore.id)}
              onEdit={chore.status !== 2 ? () => onEdit(chore) : undefined}
              onAssign={chore.status !== 2 ? onAssign : undefined}
              members={members}
              onToggleChecklistItem={onToggleChecklistItem}
              onAddChecklistItem={onAddChecklistItem}
              onDeleteChecklistItem={onDeleteChecklistItem}
              onAddGoal={onAddGoal}
              goals={goals}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChoreSection({ title, count, emoji, defaultCollapsed, children }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed || false);

  return (
    <div className="chore-section">
      <button
        className="chore-section-header"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
      >
        <span>{emoji} {title} ({count})</span>
        <span className="chore-section-arrow">{collapsed ? '▸' : '▾'}</span>
      </button>
      {!collapsed && <div className="chore-list">{children}</div>}
    </div>
  );
}

export default ChoreBoard;
